(function () {
  'use strict';

  const CAROUSEL_ROOT_SELECTOR = '.wizup-app, .vizup-app';
  const CAROUSEL_VIDEO_PLAY_SELECTOR =
    '.video-content-wrapper, .viz-video-thumbnail, .vizup-cursor-pointer';
  const CAROUSEL_VIDEO_EXCLUDE_SELECTOR =
    '.tagged-product-container, .shopping-icon-container, .product-info-container, .viz-product-tag';
  // NEVER include bare `.vizup-app-container` — it also exists on the carousel and
  // querySelector would return the carousel, locking every ATC to video idx 0.
  const PLAYER_ROOT_SELECTOR = '.sg-fc.wizup-app, .sg-fc.vizup-app';
  const DEFAULT_SECTION_NAME = 'Shop By Videos';
  const STORAGE_KEY = 'mpb_shop_by_video_attribution';
  const PENDING_ATC_KEY = 'mpb_shop_by_video_pending_atc';
  const NA = 'NA';
  const ATC_WINDOW_MS = 30 * 60 * 1000;
  const SUPPRESS_ULTIMATE_MS = 3000;
  const DEDUPE_ADD_TO_CART_MS = 1500;
  const DEDUPE_VIDEO_CLICK_MS = 3000;
  const DEDUPE_VIDEO_ATC_MS = 2000;

  window.dataLayer = window.dataLayer || [];
  window.MPB = window.MPB || {};

  let pendingAddToCart = null;
  let suppressUltimateUntil = 0;
  let lastAddToCartSignature = '';
  let lastAddToCartAt = 0;
  let lastVideoClickAt = 0;
  let lastVideoClickKey = '';
  let lastVideoAddToCartAt = 0;
  let lastVideoAddToCartKey = '';

  function safeText(value) {
    return (value || '').toString().trim();
  }

  function isCarouselWidget(root) {
    if (!root) return false;

    // Fullscreen player also uses wizup-app — exclude it.
    if (root.classList.contains('sg-fc') || root.classList.contains('vizup-app-container')) {
      return false;
    }

    const className = root.className?.toString() || '';
    if (/sg-slider|sg-floatingpop/.test(className)) return true;

    // Fallback: any non-player wizup/vizup root counts as carousel.
    return root.matches(CAROUSEL_ROOT_SELECTOR);
  }

  function isVizupPlayerOpen() {
    if (!window.location.hash.toLowerCase().includes('vizup-player')) return false;

    const player = getPlayerRoot();
    if (!player) return false;

    const style = window.getComputedStyle(player);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (parseFloat(style.opacity || '1') === 0) return false;

    return true;
  }

  function getPlayerRoot() {
    const root = document.querySelector(PLAYER_ROOT_SELECTOR);
    // Guard: player shell must be the fullscreen `.sg-fc` widget, not carousel chrome.
    if (root && root.classList.contains('sg-fc')) return root;
    return null;
  }

  function isInsidePlayerShell(target) {
    if (!(target instanceof Element)) return false;
    const playerRoot = getPlayerRoot();
    return !!(playerRoot && playerRoot.contains(target));
  }

  function isInsideOpenPlayer(target) {
    if (!(target instanceof Element)) return false;
    if (!isVizupPlayerOpen()) return false;
    if (isInsidePlayerShell(target)) return true;
    return !getCarouselRoot(target);
  }

  function getCarouselRoot(element) {
    if (!(element instanceof Element)) return null;

    const root = element.closest(CAROUSEL_ROOT_SELECTOR);
    if (!root || !isCarouselWidget(root)) return null;

    return root;
  }

  function getTrackingRoot(element) {
    if (!(element instanceof Element)) return null;

    const carouselRoot = getCarouselRoot(element);
    if (carouselRoot) return carouselRoot;

    if (isVizupPlayerOpen()) {
      return getPlayerRoot();
    }

    return null;
  }

  function getSectionName(root) {
    const widgetRoot = document.querySelector(CAROUSEL_ROOT_SELECTOR);
    const sectionAnchor = widgetRoot?.closest('.shopify-section') || root?.closest?.('.shopify-section');

    if (sectionAnchor) {
      const heading = sectionAnchor.querySelector('.section-title, h1, h2');
      const headingText = safeText(heading?.textContent);
      if (headingText) return headingText;
    }

    const previousSection = widgetRoot?.closest('.shopify-section')?.previousElementSibling;
    const previousHeading = previousSection?.querySelector('.section-title, h1, h2');
    const previousHeadingText = safeText(previousHeading?.textContent);
    if (previousHeadingText) return previousHeadingText;

    return DEFAULT_SECTION_NAME;
  }

  function isCarouselControl(element) {
    return !!element.closest(
      '.viz-navigation-arrow-left, .viz-navigation-arrow-right, .carousel-arrow-left, .carousel-arrow-right, [class*="navigation-arrow"]'
    );
  }

  function isVizupAddToCartButton(element, root) {
    const button = element.closest('button, [role="button"], a');
    if (!button || !root || !root.contains(button)) return false;

    // Whole-card wrappers can include "Add to cart" text in descendants — ignore those.
    if (button.querySelector('.video-content-wrapper, .viz-video-thumbnail, video, .tagged-product-container')) {
      return false;
    }

    const label = safeText(button.textContent).toLowerCase();
    const className = (button.className || '').toString().toLowerCase();
    const inProductArea = !!button.closest(
      '[class*="product"], [class*="viz-product"], [class*="vizup-product"], [class*="player"]'
    );

    if (/add\s*to\s*cart/.test(label)) return true;
    if (className.includes('add-to-cart') || className.includes('addtocart') || className.includes('add_to_cart')) {
      return true;
    }

    if (isVizupPlayerOpen() || root.matches?.('.sg-fc, .vizup-app-container')) {
      if (label === 'add' || label === 'add to bag' || label === 'buy now') return true;
      if (inProductArea && /^add\b/.test(label) && label.length < 24) return true;
      if (className.includes('viz') && className.includes('add')) return true;
    }

    return false;
  }

  function getVideoCard(element, root) {
    if (!(element instanceof Element)) return root || null;

    // Only use the player active *video* slide when the interaction is inside the player.
    // Blind `.swiper-slide-active` often hits the product swiper, not the video swiper.
    if (isInsidePlayerShell(element) || (isVizupPlayerOpen() && !getCarouselRoot(element))) {
      const activeSlide = getActivePlayerSlide();
      if (activeSlide) return activeSlide;
    }

    const mediaWrapper = element.closest('.video-content-wrapper, .viz-video-thumbnail, [data-idx]');
    if (mediaWrapper) {
      const withTag = mediaWrapper.closest('.swiper-slide, [class*="video-card"], [class*="reel"]');
      if (withTag?.querySelector?.('.tagged-product-container, .viz-product-tag, .product-title')) {
        return withTag;
      }

      const parent = mediaWrapper.parentElement;
      if (parent?.querySelector?.('.tagged-product-container, .viz-product-tag, .product-title')) {
        return parent;
      }

      return mediaWrapper;
    }

    return (
      element.closest(
        '.swiper-slide, [class*="video-card"], [class*="reel"], .viz-product-image-container'
      ) || root
    );
  }

  function getScopedText(root, selectors) {
    if (!root?.querySelector) return '';

    for (const selector of selectors) {
      const nodes = root.querySelectorAll(selector);
      for (const node of nodes) {
        const text = safeText(node.textContent);
        if (text) return text;
      }
    }

    return '';
  }

  function getVideoUrlFromCard(card) {
    if (!card?.querySelector) return '';

    const videos = card.querySelectorAll('video');
    for (const videoEl of videos) {
      const sourceEl = videoEl.querySelector('source');
      const url =
        safeText(videoEl.currentSrc) ||
        safeText(videoEl.src) ||
        safeText(sourceEl?.src) ||
        safeText(videoEl.getAttribute('poster'));
      if (url) return url;
    }

    const thumb = card.querySelector('.viz-video-thumbnail, img[data-src], img[src]');
    return (
      safeText(thumb?.getAttribute('data-src')) ||
      safeText(thumb?.currentSrc) ||
      safeText(thumb?.src) ||
      safeText(thumb?.getAttribute('data-poster')) ||
      ''
    );
  }

  function getActivePlayerSlide() {
    const playerRoot = getPlayerRoot();
    if (!playerRoot) return null;

    // There are multiple swipers in the player (video + products). Prefer the video slide.
    const activeWithVideo = playerRoot
      .querySelector('.swiper-slide-active video')
      ?.closest('.swiper-slide');
    if (activeWithVideo) return activeWithVideo;

    const playingVideo = Array.from(playerRoot.querySelectorAll('video')).find(
      (video) => !video.paused && (video.currentSrc || video.src)
    );
    if (playingVideo) return playingVideo.closest('.swiper-slide');

    const anyActiveVideoSlide = Array.from(playerRoot.querySelectorAll('.swiper-slide-active')).find(
      (slide) => slide.querySelector('video')
    );
    if (anyActiveVideoSlide) return anyActiveVideoSlide;

    return null;
  }

  function getProductCardAroundButton(button) {
    if (!(button instanceof Element)) return null;

    // Prefer the product swiper slide (no <video>) — video slides nest product UI and
    // would resolve to the first product title on that reel.
    const slide = button.closest('.swiper-slide');
    if (slide && !slide.querySelector('video')) return slide;

    const productContent =
      button.closest('.viz-product-content') || button.closest('[class*="product-content"]');
    if (productContent && !productContent.querySelector('video')) return productContent;

    let node = button.parentElement;
    while (node && node !== document.body) {
      if (!node.querySelector?.('video')) {
        const hasTitle = node.querySelector?.(
          '.vizup-font-semibold, .product-title, a[href*="/products/"]'
        );
        const textLen = safeText(node.textContent).length;
        if (hasTitle && textLen > 0 && textLen < 220) return node;
      }
      node = node.parentElement;
    }

    return button.parentElement;
  }

  function extractTitleFromProductCard(card) {
    if (!card) return '';

    const titleNode = card.querySelector(
      '.vizup-py-1.vizup-font-semibold, .vizup-font-semibold.vizup-line-clamp-1, .vizup-font-semibold, .product-title'
    );
    const titleText = safeText(titleNode?.textContent);
    if (titleText && !/^add(\s+to\s+cart)?$/i.test(titleText) && !/^₹/.test(titleText)) {
      return titleText;
    }

    const link = card.querySelector('a[href*="/products/"]');
    const linkText = safeText(link?.textContent);
    if (linkText && !/^add(\s+to\s+cart)?$/i.test(linkText) && linkText.length < 120) {
      return linkText;
    }

    const raw = safeText(card.textContent)
      .replace(/add\s*to\s*cart/gi, '')
      .replace(/₹[\d,]+(\.\d+)?/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    return raw.slice(0, 120);
  }

  function extractPlayerAddToCartMetadata(atcButton) {
    const playerRoot = getPlayerRoot();
    const button =
      (atcButton instanceof Element && atcButton.closest('button, [role="button"], a')) || atcButton;
    const productCard = getProductCardAroundButton(button);
    const productTitle = extractTitleFromProductCard(productCard);
    const productLink =
      productCard?.querySelector?.('a[href*="/products/"]') ||
      (button instanceof Element ? button.closest('a[href*="/products/"]') : null);
    const clickUrl = safeText(productLink?.href) || NA;

    const activeSlide = getActivePlayerSlide();
    // Player CDN URLs often differ from carousel preview URLs — do not fall back to
    // the first <video> in the player root (that always yields idx 0).
    const videoUrl = getVideoUrlFromCard(activeSlide) || getActivePlayerVideoUrl() || NA;
    const idx =
      safeText(activeSlide?.getAttribute('data-swiper-slide-index')) ||
      safeText(activeSlide?.getAttribute('data-idx')) ||
      NA;

    // Prefer idx match. URL match is unreliable (player vs carousel different files).
    const matchedCard = findCarouselCardByIdx(idx === NA ? null : idx);
    const cardMeta = matchedCard
      ? extractVideoMetadata(matchedCard, getCarouselRoot(matchedCard) || matchedCard)
      : null;

    // Prefer the product being added (ATC card), then carousel title for this video idx.
    const videoName =
      productTitle ||
      (cardMeta?.video_name && cardMeta.video_name !== NA ? cardMeta.video_name : '') ||
      NA;

    return {
      video_name: videoName || NA,
      video_id: (idx !== NA ? idx : null) || (cardMeta?.video_id !== NA ? cardMeta?.video_id : null) || NA,
      video_url:
        (videoUrl !== NA ? videoUrl : null) ||
        (cardMeta?.video_url && cardMeta.video_url !== NA ? cardMeta.video_url : null) ||
        NA,
      section_name: getSectionName(getCarouselRoot(matchedCard) || playerRoot) || DEFAULT_SECTION_NAME,
      click_url: clickUrl !== NA ? clickUrl : cardMeta?.click_url || NA
    };
  }

  function getActivePlayerVideoUrl() {
    const slide = getActivePlayerSlide();
    if (slide) return getVideoUrlFromCard(slide) || '';

    const playerRoot = getPlayerRoot();
    if (!playerRoot) return '';

    const playing = Array.from(playerRoot.querySelectorAll('video')).find(
      (video) => !video.paused && (video.currentSrc || video.src)
    );
    if (playing) {
      return (
        safeText(playing.currentSrc) ||
        safeText(playing.src) ||
        safeText(playing.querySelector('source')?.src) ||
        ''
      );
    }

    return '';
  }

  function urlsLikelyMatch(a, b) {
    if (!a || !b || a === NA || b === NA) return false;
    if (a === b) return true;

    const fileA = a.split('?')[0].split('/').pop();
    const fileB = b.split('?')[0].split('/').pop();
    return !!(fileA && fileB && fileA === fileB);
  }

  function findCarouselCardByVideoUrl(videoUrl) {
    if (!videoUrl || videoUrl === NA) return null;

    const carousels = document.querySelectorAll(CAROUSEL_ROOT_SELECTOR);
    for (const carousel of carousels) {
      if (!isCarouselWidget(carousel)) continue;

      const mediaNodes = carousel.querySelectorAll('video, source, .viz-video-thumbnail, img[data-src], img[src]');
      for (const node of mediaNodes) {
        const candidate =
          safeText(node.currentSrc) ||
          safeText(node.src) ||
          safeText(node.getAttribute('src')) ||
          safeText(node.getAttribute('data-src')) ||
          safeText(node.getAttribute('data-poster'));

        if (!urlsLikelyMatch(videoUrl, candidate)) continue;

        return (
          node.closest('.video-content-wrapper')?.parentElement ||
          node.closest('[data-idx]')?.parentElement ||
          node.closest('.swiper-slide, [class*="video-card"], [class*="reel"]') ||
          node.closest('.video-content-wrapper')
        );
      }
    }

    return null;
  }

  function findCarouselCardByIdx(idx) {
    if (idx === '' || idx == null) return null;

    const carousels = document.querySelectorAll(CAROUSEL_ROOT_SELECTOR);
    for (const carousel of carousels) {
      if (!isCarouselWidget(carousel)) continue;
      const match = carousel.querySelector(`[data-idx="${idx}"]`);
      if (!match) continue;
      return match.closest('.video-content-wrapper')?.parentElement || match.parentElement || match;
    }

    return null;
  }

  function getProductPanelFromElement(element) {
    if (!(element instanceof Element)) return null;
    return (
      element.closest('.viz-product-content') ||
      element.closest('[class*="product-content"]') ||
      element.closest('[class*="HScroll"], [class*="product-detail"], [class*="ProductDetail"]') ||
      null
    );
  }

  function extractProductPanelMetadata(panel) {
    if (!panel) {
      return { product_title: '', click_url: '' };
    }

    const productLink = panel.querySelector('a[href*="/products/"]');
    const clickUrl = safeText(productLink?.href);

    const titleCandidates = panel.querySelectorAll(
      '.vizup-py-1.vizup-font-semibold, .vizup-font-semibold.vizup-line-clamp-1, .vizup-font-semibold, .product-title, a[href*="/products/"]'
    );

    let productTitle = '';
    for (const node of titleCandidates) {
      const text = safeText(node.textContent);
      const lower = text.toLowerCase();
      if (!text || text.length < 3) continue;
      if (/^add(\s+to\s+cart)?$/.test(lower)) continue;
      if (/^₹|^rs\.?/i.test(text)) continue;
      if (/^\d+%/.test(text)) continue;
      productTitle = text;
      break;
    }

    if (!productTitle) {
      productTitle = safeText(panel.querySelector('img[alt]')?.getAttribute('alt'));
      if (/^product$/i.test(productTitle)) productTitle = '';
    }

    return {
      product_title: productTitle,
      click_url: clickUrl
    };
  }

  function titlesLikelyMatch(a, b) {
    const left = safeText(a).toLowerCase();
    const right = safeText(b).toLowerCase();
    if (!left || !right || left === 'na' || right === 'na') return false;
    if (left === right) return true;
    return left.includes(right) || right.includes(left);
  }

  function extractClickUrlFromRoot(root) {
    if (!root?.querySelector) return '';

    const direct = root.querySelector('a[href*="/products/"]');
    if (direct?.href) return safeText(direct.href);

    return '';
  }

  // Carousel cards have no product <a> — URLs only appear in the fullscreen player.
  function extractClickUrlFromPlayer(preferredTitle) {
    const playerRoot = getPlayerRoot();
    if (!playerRoot) return '';

    const links = Array.from(playerRoot.querySelectorAll('a[href*="/products/"]'));
    if (!links.length) return '';

    for (const link of links) {
      const linkText = safeText(link.textContent);
      if (titlesLikelyMatch(preferredTitle, linkText)) return safeText(link.href);

      const card = link.closest('.swiper-slide') || link.parentElement;
      const cardTitle = getScopedText(card, [
        '.vizup-font-semibold',
        '.product-title',
        '.vizup-py-1.vizup-font-semibold'
      ]);
      if (titlesLikelyMatch(preferredTitle, cardTitle)) return safeText(link.href);
    }

    // Prefer a product slide (no video) over a video slide that nests many products.
    const productSlides = Array.from(playerRoot.querySelectorAll('.swiper-slide-active')).filter(
      (slide) => !slide.querySelector('video')
    );
    for (const slide of productSlides) {
      const href = extractClickUrlFromRoot(slide);
      if (href) return href;
    }

    return safeText(links[0].href);
  }

  function extractVideoMetadata(card, root) {
    const searchRoot = card || root;
    const idxEl =
      (card instanceof Element && card.matches('[data-idx]') ? card : null) ||
      card?.querySelector?.('[data-idx]') ||
      (root instanceof Element ? root.querySelector('[data-idx]') : null);

    const videoName =
      getScopedText(searchRoot, [
        '.custom-title-text',
        '.tagged-product-container .product-title',
        '.viz-product-tag .product-title',
        '.product-title',
        '.viz-product-tag'
      ]) || NA;

    const videoId =
      safeText(idxEl?.getAttribute('data-idx')) ||
      safeText(card?.getAttribute('data-video-id')) ||
      safeText(card?.getAttribute('data-id')) ||
      NA;

    const videoUrl = getVideoUrlFromCard(searchRoot) || NA;

    const clickUrl =
      extractClickUrlFromRoot(searchRoot) ||
      extractClickUrlFromPlayer(videoName) ||
      NA;

    return {
      video_name: videoName || NA,
      video_id: videoId || NA,
      video_url: videoUrl || NA,
      section_name: getSectionName(root),
      click_url: clickUrl
    };
  }

  function findStoredVideoByUrl(videoUrl) {
    const attribution = readAttribution();
    if (!attribution?.videos?.length || !videoUrl || videoUrl === NA) return null;
    return attribution.videos.find((video) => urlsLikelyMatch(video.video_url, videoUrl)) || null;
  }

  function readAttribution() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeAttribution(data) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      // Storage unavailable
    }
  }

  function setPendingAddToCart(metadata) {
    pendingAddToCart = metadata;
    try {
      sessionStorage.setItem(
        PENDING_ATC_KEY,
        JSON.stringify({
          ...metadata,
          set_at: Date.now()
        })
      );
    } catch (error) {
      // Storage unavailable
    }
  }

  function restorePendingAddToCart() {
    if (pendingAddToCart) return pendingAddToCart;

    try {
      const raw = sessionStorage.getItem(PENDING_ATC_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed?.set_at || Date.now() - parsed.set_at > ATC_WINDOW_MS) {
        sessionStorage.removeItem(PENDING_ATC_KEY);
        return null;
      }

      pendingAddToCart = parsed;
      return pendingAddToCart;
    } catch (error) {
      return null;
    }
  }

  function clearPendingAddToCart() {
    pendingAddToCart = null;
    try {
      sessionStorage.removeItem(PENDING_ATC_KEY);
    } catch (error) {
      // Storage unavailable
    }
  }

  function shouldAttributeAddToCart() {
    restorePendingAddToCart();
    if (pendingAddToCart) return true;

    const attribution = readAttribution();
    if (!attribution?.last_video || !attribution.updated_at) return false;

    const age = Date.now() - new Date(attribution.updated_at).getTime();
    if (age > ATC_WINDOW_MS) return false;

    return isVizupPlayerOpen();
  }

  function markSuppressUltimateDataLayer() {
    if (shouldAttributeAddToCart() || isVizupPlayerOpen()) {
      suppressUltimateUntil = Date.now() + SUPPRESS_ULTIMATE_MS;
    }
  }

  function shouldSuppressUltimateDataLayerAddToCart() {
    return Date.now() < suppressUltimateUntil;
  }

  function consumeAddToCartSource() {
    if (!shouldAttributeAddToCart()) return null;
    clearPendingAddToCart();
    return 'video';
  }

  function rememberVideoEngagement(metadata) {
    const existing = readAttribution() || {
      source: 'video',
      section_name: metadata.section_name,
      videos: [],
      variant_ids: []
    };

    existing.last_video = metadata;
    existing.section_name = metadata.section_name;
    existing.updated_at = new Date().toISOString();

    const videoKey = `${metadata.video_id}|${metadata.video_name}`;
    if (!existing.videos.some((video) => `${video.video_id}|${video.video_name}` === videoKey)) {
      existing.videos.push(metadata);
    }

    writeAttribution(existing);
  }

  function rememberVariantFromAddToCart(items) {
    if (!Array.isArray(items) || !items.length) return;

    const attribution = readAttribution() || {
      source: 'video',
      section_name: DEFAULT_SECTION_NAME,
      videos: [],
      variant_ids: []
    };

    for (const item of items) {
      const variantId = item.variant_id?.toString();
      if (variantId && !attribution.variant_ids.includes(variantId)) {
        attribution.variant_ids.push(variantId);
      }
    }

    attribution.updated_at = new Date().toISOString();
    writeAttribution(attribution);
    syncCartAttribution(attribution);
  }

  function syncCartAttribution(attribution) {
    const payload = {
      attributes: {
        shop_by_video_source: 'video',
        shop_by_video_section: attribution.section_name || DEFAULT_SECTION_NAME,
        shop_by_video_variant_ids: (attribution.variant_ids || []).join(','),
        shop_by_video_last_video_name: attribution.last_video?.video_name || NA
      }
    };

    fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(() => {
      // Non-blocking attribution sync
    });
  }

  function getVideoClickKey(metadata) {
    return [metadata.video_url, metadata.video_id, metadata.video_name].join('|');
  }

  function isDuplicateVideoClick(metadata) {
    const key = getVideoClickKey(metadata);
    const now = Date.now();
    const isDuplicate = key === lastVideoClickKey && now - lastVideoClickAt < DEDUPE_VIDEO_CLICK_MS;

    if (!isDuplicate) {
      lastVideoClickKey = key;
      lastVideoClickAt = now;
    }

    return isDuplicate;
  }

  function publishCustomerEvent(eventName, payload) {
    if (!window.Shopify?.analytics?.publish) return false;

    // Defer like other theme analytics publishes to avoid sandbox timing issues.
    setTimeout(() => {
      try {
        Shopify.analytics.publish(eventName, payload);
      } catch (error) {
        // Error handled silently
      }
    }, 0);

    return true;
  }

  let videoClickEnrichTimer = null;

  function commitVideoClick(metadata) {
    if (isDuplicateVideoClick(metadata)) return;

    const payload = {
      video_name: metadata.video_name || NA,
      video_id: metadata.video_id || NA,
      video_url: metadata.video_url || NA,
      section_name: metadata.section_name || DEFAULT_SECTION_NAME,
      page_url: window.location.href,
      page_title: document.title,
      click_url: metadata.click_url || NA
    };

    // Same path as header_menu_click / email_click → Custom Pixel → dataLayer / GTM
    publishCustomerEvent('video_click', payload);

    rememberVideoEngagement(metadata);
    setPendingAddToCart(metadata);
  }

  function pushVideoClick(metadata) {
    const base = {
      ...metadata,
      click_url:
        (metadata.click_url && metadata.click_url !== NA ? metadata.click_url : null) ||
        extractClickUrlFromPlayer(metadata.video_name) ||
        NA
    };

    // Carousel cards have no product <a>; wait briefly for the player product link.
    if (base.click_url !== NA) {
      if (videoClickEnrichTimer) {
        clearInterval(videoClickEnrichTimer);
        videoClickEnrichTimer = null;
      }
      commitVideoClick(base);
      return;
    }

    if (videoClickEnrichTimer) {
      clearInterval(videoClickEnrichTimer);
      videoClickEnrichTimer = null;
    }

    let attempts = 0;
    videoClickEnrichTimer = setInterval(() => {
      attempts += 1;
      const clickUrl = extractClickUrlFromPlayer(base.video_name);
      if (clickUrl || attempts >= 15) {
        clearInterval(videoClickEnrichTimer);
        videoClickEnrichTimer = null;
        commitVideoClick({ ...base, click_url: clickUrl || NA });
      }
    }, 100);
  }

  function getVideoAddToCartKey(metadata) {
    return [metadata.video_url, metadata.video_id, metadata.video_name, metadata.click_url].join('|');
  }

  function isDuplicateVideoAddToCart(metadata) {
    const key = getVideoAddToCartKey(metadata);
    const now = Date.now();
    const isDuplicate = key === lastVideoAddToCartKey && now - lastVideoAddToCartAt < DEDUPE_VIDEO_ATC_MS;

    if (!isDuplicate) {
      lastVideoAddToCartKey = key;
      lastVideoAddToCartAt = now;
    }

    return isDuplicate;
  }

  function resolveVideoAddToCartMetadata(preferredMetadata, atcButton) {
    const liveFromPlayer = atcButton ? extractPlayerAddToCartMetadata(atcButton) : null;
    const playerRoot = getPlayerRoot();
    const fallbackLive =
      !liveFromPlayer && isVizupPlayerOpen()
        ? extractPlayerAddToCartMetadata(playerRoot?.querySelector('button, [role="button"], a') || playerRoot)
        : null;
    const live = liveFromPlayer || fallbackLive;
    const base = preferredMetadata || restorePendingAddToCart() || readAttribution()?.last_video || {};

    // Prefer live player/carousel data. Only use pending attribution for missing fields.
    return {
      video_name: (live?.video_name && live.video_name !== NA ? live.video_name : null) || base.video_name || NA,
      video_id: (live?.video_id && live.video_id !== NA ? live.video_id : null) || base.video_id || NA,
      video_url: (live?.video_url && live.video_url !== NA ? live.video_url : null) || base.video_url || NA,
      section_name: live?.section_name || base.section_name || DEFAULT_SECTION_NAME,
      click_url: (live?.click_url && live.click_url !== NA ? live.click_url : null) || base.click_url || NA
    };
  }

  function pushVideoAddToCart(preferredMetadata, atcButton) {
    const metadata = resolveVideoAddToCartMetadata(preferredMetadata, atcButton);
    if (isDuplicateVideoAddToCart(metadata)) return;

    const payload = {
      add_to_cart_source: 'video',
      video_name: metadata.video_name || NA,
      video_id: metadata.video_id || NA,
      video_url: metadata.video_url || NA,
      section_name: metadata.section_name || DEFAULT_SECTION_NAME,
      page_url: window.location.href,
      page_title: document.title,
      click_url: metadata.click_url || NA
    };

    // Same path as header_menu_click / email_click → Custom Pixel → dataLayer / GTM
    publishCustomerEvent('video_add_to_cart', payload);

    rememberVideoEngagement(metadata);
    setPendingAddToCart(metadata);
  }

  function getPurchaseAttribution() {
    const attribution = readAttribution();
    if (!attribution?.variant_ids?.length) return null;

    return {
      source: 'video',
      section_name: attribution.section_name || DEFAULT_SECTION_NAME,
      variant_ids: attribution.variant_ids,
      videos: attribution.videos || [],
      last_video: attribution.last_video || null
    };
  }

  function getAddToCartSignature(payload) {
    const items = payload.ecommerce?.items || [];
    return items
      .map((item) => `${item.item_id || item.id || item.item_name}:${item.quantity || 1}`)
      .sort()
      .join('|');
  }

  function isDuplicateAddToCart(payload) {
    const signature = getAddToCartSignature(payload);
    if (!signature) return false;

    const now = Date.now();
    const isDuplicate =
      signature === lastAddToCartSignature && now - lastAddToCartAt < DEDUPE_ADD_TO_CART_MS;

    if (!isDuplicate) {
      lastAddToCartSignature = signature;
      lastAddToCartAt = now;
    }

    return isDuplicate;
  }

  function enrichAddToCartPayload(payload) {
    if (payload.add_to_cart_source === 'video') return false;

    const source = consumeAddToCartSource();
    if (!source) return false;

    payload.add_to_cart_source = source;
    return true;
  }

  function installDataLayerHook() {
    const dataLayer = window.dataLayer;
    const originalPush = dataLayer.push.bind(dataLayer);

    dataLayer.push = function () {
      const batch = [];

      for (let index = 0; index < arguments.length; index += 1) {
        const payload = arguments[index];

        if (!payload || typeof payload !== 'object') {
          batch.push(payload);
          continue;
        }

        if (payload.event === 'add_to_cart') {
          if (isDuplicateAddToCart(payload)) {
            continue;
          }

          const enriched = enrichAddToCartPayload(payload);

          if (enriched && payload.ecommerce?.items?.length) {
            rememberVariantFromAddToCart(
              payload.ecommerce.items.map((item) => ({
                variant_id: item.variant_id || item.id
              }))
            );
          }
        }

        if (payload.event === 'begin_checkout') {
          const attribution = getPurchaseAttribution();
          if (attribution) {
            payload.shop_by_video_attribution = attribution;
            payload.purchase_source = 'video';
          }
        }

        batch.push(payload);
      }

      if (!batch.length) return dataLayer.length;
      return originalPush.apply(dataLayer, batch);
    };
  }

  function isCartAddUrl(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?/.test(url);
  }

  function markCartAddFromVideo() {
    restorePendingAddToCart();

    if (!pendingAddToCart) {
      const attribution = readAttribution();
      if (attribution?.last_video && isVizupPlayerOpen()) {
        setPendingAddToCart(attribution.last_video);
      }
    }

    markSuppressUltimateDataLayer();

    // Backup only — click handler is primary. Dedupe prevents doubles.
    if (shouldAttributeAddToCart() || isVizupPlayerOpen()) {
      pushVideoAddToCart(pendingAddToCart, null);
    }
  }

  function installNetworkHooks() {
    const previousFetch = window.fetch;

    window.fetch = function () {
      const requestUrl = typeof arguments[0] === 'string' ? arguments[0] : arguments[0]?.url;

      if (isCartAddUrl(requestUrl)) {
        markCartAddFromVideo();
      }

      return previousFetch.apply(this, arguments);
    };
  }

  function isCarouselVideoPlayClick(target) {
    // Clicks inside the fullscreen player shell are never carousel play clicks.
    if (isInsidePlayerShell(target)) return false;

    const carousel = getCarouselRoot(target);
    if (!carousel) return false;

    if (isCarouselControl(target)) return false;
    if (target.closest(CAROUSEL_VIDEO_EXCLUDE_SELECTOR)) return false;
    if (isVizupAddToCartButton(target, carousel)) return false;

    return !!target.closest(
      '.video-content-wrapper, .viz-video-thumbnail, video, img.viz-video-thumbnail, .vizup-cursor-pointer'
    );
  }

  function handleDocumentClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Carousel thumbnail/play click — do NOT gate on leftover #vizup-player hash.
    if (isCarouselVideoPlayClick(target)) {
      const carousel = getCarouselRoot(target);
      const card = getVideoCard(target, carousel);
      pushVideoClick(extractVideoMetadata(card, carousel));
      return;
    }

    // Player ATC only (never video_click here).
    if (isVizupPlayerOpen() || isInsidePlayerShell(target)) {
      const trackingRoot = getTrackingRoot(target) || getPlayerRoot();
      if (trackingRoot && isVizupAddToCartButton(target, trackingRoot)) {
        const atcButton = target.closest('button, [role="button"], a');
        const metadata = extractPlayerAddToCartMetadata(atcButton || target);
        setPendingAddToCart(metadata);
        rememberVideoEngagement(metadata);
        markSuppressUltimateDataLayer();
        pushVideoAddToCart(metadata, atcButton || target);
      }
    }
  }

  function handleHashChange() {
    if (!isVizupPlayerOpen()) return;

    const playerRoot = getPlayerRoot();
    if (!playerRoot) return;

    // Fallback: if carousel click was missed, still record one video_click when player opens.
    const card = getVideoCard(playerRoot, playerRoot);
    const metadata = extractVideoMetadata(card || playerRoot, playerRoot);
    pushVideoClick(metadata);
  }

  installDataLayerHook();
  installNetworkHooks();

  document.addEventListener('click', handleDocumentClick, true);
  window.addEventListener('hashchange', handleHashChange);

  window.MPB.shopByVideo = {
    consumeAddToCartSource,
    getPurchaseAttribution,
    shouldSuppressUltimateDataLayerAddToCart,
    isPendingAddToCart: () => !!restorePendingAddToCart(),
    isVizupPlayerOpen
  };
})();
